import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  User,
  Mail,
  Phone,
  MapPin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const Beneficiaries = () => {
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingBeneficiary, setEditingBeneficiary] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    date_of_birth: "",
    employment_status: "",
    income_level: "",
    referral_source: "",
    notes: ""
  });

  useEffect(() => {
    fetchBeneficiaries();
  }, [statusFilter]);

  const fetchBeneficiaries = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/beneficiaries?${params}`);
      setBeneficiaries(response.data.items || []);
    } catch (error) {
      toast.error("Failed to fetch beneficiaries");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBeneficiaries();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBeneficiary) {
        await axios.put(`${API}/beneficiaries/${editingBeneficiary.id}`, formData);
        toast.success("Beneficiary updated successfully");
      } else {
        await axios.post(`${API}/beneficiaries`, formData);
        toast.success("Beneficiary registered successfully");
      }
      setShowDialog(false);
      resetForm();
      fetchBeneficiaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save beneficiary");
    }
  };

  const handleEdit = (beneficiary) => {
    setEditingBeneficiary(beneficiary);
    setFormData({
      first_name: beneficiary.first_name,
      last_name: beneficiary.last_name,
      email: beneficiary.email,
      phone: beneficiary.phone,
      address: beneficiary.address,
      city: beneficiary.city,
      state: beneficiary.state,
      zip_code: beneficiary.zip_code,
      date_of_birth: beneficiary.date_of_birth,
      employment_status: beneficiary.employment_status,
      income_level: beneficiary.income_level,
      referral_source: beneficiary.referral_source,
      notes: beneficiary.notes || ""
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this beneficiary?")) return;
    try {
      await axios.delete(`${API}/beneficiaries/${id}`);
      toast.success("Beneficiary deleted");
      fetchBeneficiaries();
    } catch (error) {
      toast.error("Failed to delete beneficiary");
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await axios.put(`${API}/beneficiaries/${id}`, { status: newStatus });
      toast.success(`Beneficiary ${newStatus}`);
      fetchBeneficiaries();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const resetForm = () => {
    setEditingBeneficiary(null);
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zip_code: "",
      date_of_birth: "",
      employment_status: "",
      income_level: "",
      referral_source: "",
      notes: ""
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: "bg-green-100 text-green-800 border-green-200",
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      rejected: "bg-red-100 text-red-800 border-red-200"
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.pending}>
        {status}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="beneficiaries-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Beneficiaries
          </h1>
          <p className="text-slate-500 mt-1">Manage program participants and eligibility</p>
        </div>
        <Button 
          className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
          onClick={() => { resetForm(); setShowDialog(true); }}
          data-testid="add-beneficiary-btn"
        >
          <Plus className="w-4 h-4" />
          Register Beneficiary
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" variant="secondary" data-testid="search-btn">Search</Button>
            </form>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Employment Status</TableHead>
                <TableHead>Vouchers</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="animate-pulse">Loading...</div>
                  </TableCell>
                </TableRow>
              ) : beneficiaries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No beneficiaries found
                  </TableCell>
                </TableRow>
              ) : (
                beneficiaries.map((beneficiary) => (
                  <TableRow key={beneficiary.id} data-testid={`beneficiary-row-${beneficiary.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-slate-500" />
                        </div>
                        <div>
                          <div className="font-medium">{beneficiary.first_name} {beneficiary.last_name}</div>
                          <div className="text-xs text-slate-500">{beneficiary.referral_source?.replace(/_/g, ' ')}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1 text-sm">
                        <div className="flex items-center gap-1 text-slate-600">
                          <Mail className="w-3 h-3" /> {beneficiary.email}
                        </div>
                        <div className="flex items-center gap-1 text-slate-500">
                          <Phone className="w-3 h-3" /> {beneficiary.phone}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-600">
                        <MapPin className="w-3 h-3" />
                        {beneficiary.city}, {beneficiary.state}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 capitalize">
                      {beneficiary.employment_status?.replace(/_/g, ' ')}
                    </TableCell>
                    <TableCell className="font-medium">
                      {beneficiary.vouchers_issued || 0}
                    </TableCell>
                    <TableCell>{getStatusBadge(beneficiary.status)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`actions-${beneficiary.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(beneficiary)}>
                            <Edit className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          {beneficiary.status === "pending" && (
                            <>
                              <DropdownMenuItem onClick={() => handleStatusUpdate(beneficiary.id, "approved")}>
                                <CheckCircle className="w-4 h-4 mr-2 text-green-600" /> Approve
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleStatusUpdate(beneficiary.id, "rejected")}>
                                <XCircle className="w-4 h-4 mr-2 text-red-600" /> Reject
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem 
                            onClick={() => handleDelete(beneficiary.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingBeneficiary ? "Edit Beneficiary" : "Register New Beneficiary"}
            </DialogTitle>
            <DialogDescription>
              {editingBeneficiary ? "Update beneficiary information" : "Enter the details of the new program participant"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900">Personal Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    required
                    data-testid="input-first-name"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    required
                    data-testid="input-last-name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                    data-testid="input-phone"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="date_of_birth">Date of Birth *</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                  required
                  data-testid="input-dob"
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900">Address</h4>
              <div>
                <Label htmlFor="address">Street Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  required
                  data-testid="input-address"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    required
                    data-testid="input-city"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    required
                    data-testid="input-state"
                  />
                </div>
                <div>
                  <Label htmlFor="zip_code">ZIP Code *</Label>
                  <Input
                    id="zip_code"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                    required
                    data-testid="input-zip"
                  />
                </div>
              </div>
            </div>

            {/* Eligibility */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900">Eligibility Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="employment_status">Employment Status *</Label>
                  <Select 
                    value={formData.employment_status} 
                    onValueChange={(v) => setFormData({...formData, employment_status: v})}
                  >
                    <SelectTrigger data-testid="select-employment">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unemployed">Unemployed</SelectItem>
                      <SelectItem value="underemployed">Underemployed</SelectItem>
                      <SelectItem value="displaced_worker">Displaced Worker</SelectItem>
                      <SelectItem value="job_training">In Job Training</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="income_level">Income Level *</Label>
                  <Select 
                    value={formData.income_level} 
                    onValueChange={(v) => setFormData({...formData, income_level: v})}
                  >
                    <SelectTrigger data-testid="select-income">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="very_low">Very Low (Below 100% FPL)</SelectItem>
                      <SelectItem value="low">Low (100-150% FPL)</SelectItem>
                      <SelectItem value="moderate">Moderate (150-200% FPL)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="referral_source">Referral Source *</Label>
                <Select 
                  value={formData.referral_source} 
                  onValueChange={(v) => setFormData({...formData, referral_source: v})}
                >
                  <SelectTrigger data-testid="select-referral">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="workforce_agency">Workforce Development Agency</SelectItem>
                    <SelectItem value="community_org">Community Organization</SelectItem>
                    <SelectItem value="social_services">Social Services</SelectItem>
                    <SelectItem value="career_center">Career Center</SelectItem>
                    <SelectItem value="self_referral">Self-Referral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional notes about the beneficiary..."
                  data-testid="input-notes"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700" data-testid="submit-btn">
                {editingBeneficiary ? "Update" : "Register"} Beneficiary
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Beneficiaries;
